import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { registerFormStyles, createDynamicStyles } from '../styles/components/RegisterForm.styles';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/ToastManager';
import { useRegistrationFlow } from '../context/RegistrationFlowContext';
import { useNavigationBlock } from '../hooks/useNavigationBlock';
import NavigationBlockModal from '../components/ui/NavigationBlockModal';
import { detectCardBrand } from '../utils/cardBrandDetector';
import PaymentProcessing from '../components/ui/PaymentProcessing';
import {
  DynamicInitialCostNotice,
  loadingStyles,
  errorStyles
} from '../styles/components/CreditCardForm.styles';
import { getEnvironmentConfig, ENDPOINTS } from '../config/apiEndpoints.js';
import { initialCostSettings, storeUtils } from '../data/storeData.js';
import { PersonalInfoConsent, BUTTON_TEXT, ERROR_TRANSLATIONS } from '../data/creditCardData.js';
import { ROUTES } from '../config/routes';
import { PREFECTURES, getPrefectureCode } from '../data/prefectures';
import { formatPostalCode } from '../utils/formValidation';
import useAddressSearch from '../hooks/useAddressSearch';
import PostalCodeInput from '../components/ui/PostalCodeInput';
import { 
  formatCardNumber, 
  formatCVV, 
  formatCardholderName,
  formatZipCode 
} from '../utils/creditCardFormatters';
import { 
  validateCardNumber, 
  validateExpiry, 
  validateCvv, 
  validateCardholderName,
  validateBillingZipCode,
  validateBillingCity,
  validateBillingAddress,
  validateBillingState
} from '../utils/creditCardValidators';
import {
  generatePaygentToken,
  checkPaygentTokenLoaded,
  display3DSScreen
} from '../utils/paygentTokenHelper';


/**
 * 店舗名から初期費用情報を取得
 * @param {string} storeName - 店舗名
 * @returns {Object} 初期費用情報
 */
const getStoreCostInfo = (storeName) => {
  return storeUtils.getStoreCostInfo(storeName);
};

/**
 * CSRFトークン取得を待機
 * @param {Function} getCsrfTokenFromCookie - CSRFトークン取得関数
 * @param {number} maxAttempts - 最大試行回数
 * @param {number} interval - 試行間隔(ミリ秒)
 * @returns {Promise<string|null>} CSRFトークン
 */
const waitForCsrfToken = async (getCsrfTokenFromCookie, maxAttempts = 15, interval = 200) => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const token = await getCsrfTokenFromCookie(false);
    if (token) {
      return token;
    }
    await new Promise(resolve => setTimeout(resolve, interval));
  }
  return null;
};

/**
 * 会員情報をAPIから取得(共通関数)
 * @param {Function} getCsrfTokenFromCookie - CSRFトークン取得関数
 * @returns {Promise<Object>} { memberData, memberId }
 */
const fetchMemberDataFromAPI = async (getCsrfTokenFromCookie) => {
  const csrfToken = await getCsrfTokenFromCookie(true);
  if (!csrfToken) {
    throw new Error('CSRFトークンの取得に失敗しました');
  }

  const config = getEnvironmentConfig();
  const apiUrl = `${config.baseUrl}${ENDPOINTS.MEMBER.ME}`;

  const response = await fetch(apiUrl, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken
    }
  });

  const data = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.message || '会員情報の取得に失敗しました');
  }

  const memberData = data.member || data.user || data;
  const memberId = memberData.memberId || memberData.id || memberData.member_id;

  if (!memberId) {
    console.error('[CREDIT CARD FORM] 会員IDが見つかりません');
    throw new Error('会員IDがレスポンスに含まれていません');
  }

  return { memberData, memberId };
};

/**
 * エラーメッセージを日本語化
 * @param {string} errorMessage - エラーメッセージ
 * @returns {string} 翻訳されたエラーメッセージ
 */
const translateErrorMessage = (errorMessage) => {
  for (const [key, translation] of Object.entries(ERROR_TRANSLATIONS)) {
    if (errorMessage.includes(key)) {
      return translation;
    }
  }
  return errorMessage;
};

/**
 * クレジットカード情報入力フォームコンポーネント
 */
const CreditCardForm = ({ onCreditCardComplete }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const { completeStep, REGISTRATION_STEPS, isInRegistrationFlow } = useRegistrationFlow();
  const { 
    getCsrfTokenFromCookie, 
    isAuthenticated, 
    clearUserSessionForCardRegistration,
    loading 
  } = useAuth();

  /**
   * フォーム送信中状態の管理
   */
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComposing, setIsComposing] = useState(false);

  /**
   * バリデーションエラーの状態管理
   */
  const [errors, setErrors] = useState({});

  /**
   * 会員情報の状態管理
   */
  const [memberInfo, setMemberInfo] = useState({
    loading: true,
    data: null,
    error: null,
    memberId: null
  });

  /**
   * クレジットカード情報の状態管理
   */
  const [cardData, setCardData] = useState({
    cardNumber: '',
    cardholderName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    billingZipCode: '',
    billingState: '',
    billingCity: '',
    billingAddress: '',
    paymentAmount: initialCostSettings.default.cost,
    useMemberAddress: true
  });

  /**
   * 店舗費用情報の状態管理
   */
  const [storeCostInfo, setStoreCostInfo] = useState(initialCostSettings.default);

  /**
   * 決済処理中画面の状態管理
   */
  const [processingState, setProcessingState] = useState({
    isProcessing: false,
    cardBrand: 'UNKNOWN',
    message: '決済処理中...'
  });

  /**
   * 登録フロー中の離脱防止
   * 条件:
   * 1. 登録フロー中である (isInRegistrationFlow() === true)
   * 2. 送信中でない (!isSubmitting)
   * 3. ローディング中でない (!memberInfo.loading)
   * 
   * マイページから来た場合(isInRegistrationFlow() === false)はブロックしない
   */
  const shouldBlockNavigation = isInRegistrationFlow() && !isSubmitting && !memberInfo.loading;

  const { isModalOpen, handleConfirm, handleCancel } = useNavigationBlock(
    shouldBlockNavigation,
    () => ROUTES.TERMS
  );

  /**
   * API/meを呼び出して会員情報を取得
   */
  const fetchMemberInfo = async () => {

    if (!isAuthenticated()) {
      console.error('[CREDIT CARD FORM] ✗ 認証されていません');
      setMemberInfo(prev => ({
        ...prev,
        loading: false,
        error: '認証されていません'
      }));
      return;
    }
    setMemberInfo(prev => ({ ...prev, loading: true, error: null }));

    try {
      
      const { memberData, memberId } = await fetchMemberDataFromAPI(getCsrfTokenFromCookie);

      setMemberInfo({
        loading: false,
        data: memberData,
        error: null,
        memberId: memberId
      });

    } catch (error) {
      console.error('[CREDIT CARD FORM] 会員情報取得エラー:', error);

      setMemberInfo({
        loading: false,
        data: null,
        error: error.message || '会員情報の取得に失敗しました',
        memberId: null
      });

      showError(`会員情報の取得に失敗しました: ${error.message}`, 8000);
    }
  };

  /**
   * コンポーネント初期化時の処理
   */
  const initialize = async () => {
    // ✅ loading 中はスキップ
    if (loading) {
      return;
    }
  
    if (!isAuthenticated()) {
      setMemberInfo(prev => ({
        ...prev,
        loading: false,
        error: '認証されていません。ログインし直してください。'
      }));
      return;
    }

    // すでに会員データがある、またはエラーがある場合はスキップ
    if (memberInfo.data || memberInfo.error) {
      return;
    }

    try {

      const csrfToken = await waitForCsrfToken(getCsrfTokenFromCookie);

      if (csrfToken) {
        await fetchMemberInfo();
      } else {
        console.warn('[CREDIT CARD FORM] CSRF token not available after timeout');
        await fetchMemberInfo();
      }
    } catch (error) {
      console.error('[CREDIT CARD FORM] Initialization error:', error);
    }
  };

  /**
   * コンポーネントマウント時に初期化
   */
  useEffect(() => {
    initialize();
    // 認証状態の確定時だけ初期化したいので、関数は依存に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  /**
   * 会員情報取得時に店舗別初期費用を更新
   */
  useEffect(() => {
    if (memberInfo.data && memberInfo.data.storeName) {
      const costInfo = getStoreCostInfo(memberInfo.data.storeName);

      setStoreCostInfo(costInfo);
      setCardData(prev => ({
        ...prev,
        paymentAmount: costInfo.cost
      }));
    }
  }, [memberInfo.data]);

  /**
   * 会員情報状態変更時の処理
   */
  useEffect(() => {
    if (memberInfo.loading) {
      return;
    }

    if (memberInfo.error) {
      console.error('[CREDIT CARD FORM] 会員情報エラー:', memberInfo.error);
      showError('会員情報を取得できませんでした。ログインし直してください。', 8000);

      // 3秒後にログイン画面にリダイレクト
      setTimeout(() => {
        navigate(ROUTES.LOGIN, { replace: true });
      }, 3000);
      return;
    }

    if (!memberInfo.memberId) {
      console.warn('[CREDIT CARD FORM] 会員IDが存在しません');
      showError('会員登録が完了していません。会員登録からやり直してください。', 8000);

      // 3秒後に会員登録画面にリダイレクト  
      setTimeout(() => {
        navigate(ROUTES.REGISTER, { replace: true });
      }, 3000);
    }
  }, [memberInfo.loading, memberInfo.error, memberInfo.memberId, showError, navigate]);

  /**
   *  IMEハンドラー
   */
  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = (e) => {
    setIsComposing(false);
    const { name, value } = e.target;
    
    const formatters = {
      cardNumber: formatCardNumber,
      cvv: formatCVV,
      cardholderName: formatCardholderName,
      billingZipCode: formatZipCode
    };
    
    const formattedValue = formatters[name] ? formatters[name](value) : value;
    setCardData(prev => ({ ...prev, [name]: formattedValue }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  /**
   * 入力フィールド変更時のハンドラー
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setCardData(prev => ({ ...prev, [name]: checked }));

      // useMemberAddressがtrueになった時、請求先住所のエラーをクリア
      if (name === 'useMemberAddress' && checked) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.billingZipCode;
          delete newErrors.billingState;
          delete newErrors.billingCity;
          delete newErrors.billingAddress;
          return newErrors;
        });
      }
      return;
    }

    // IME変換中はフォーマットをスキップ
    if (isComposing) {
      setCardData(prev => ({ ...prev, [name]: value }));
      return;
    }

    const formatters = {
      cardNumber: formatCardNumber,
      cvv: formatCVV,
      cardholderName: formatCardholderName,
      billingZipCode: formatZipCode
    };

    const formattedValue = formatters[name] ? formatters[name](value) : value;
    setCardData(prev => ({ ...prev, [name]: formattedValue }));

    // エラー状態クリア
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  /**
   * フォーム入力データのクライアントサイドバリデーション
   */
  const validateFormData = () => {
    const newErrors = {};

    // カード情報のバリデーション
    const cardNumberError = validateCardNumber(cardData.cardNumber);
    if (cardNumberError) newErrors.cardNumber = cardNumberError;

    const expiryError = validateExpiry(cardData.expiryMonth, cardData.expiryYear);
    if (expiryError) newErrors.expiry = expiryError;

    const cvvError = validateCvv(cardData.cvv);
    if (cvvError) newErrors.cvv = cvvError;

    const cardholderNameError = validateCardholderName(cardData.cardholderName);
    if (cardholderNameError) newErrors.cardholderName = cardholderNameError;

    // 会員住所を使用する場合は請求先住所のバリデーションをスキップ
    if (!cardData.useMemberAddress) {

    // 請求先住所のバリデーション
    const billingZipCodeError = validateBillingZipCode(cardData.billingZipCode);
    if (billingZipCodeError) newErrors.billingZipCode = billingZipCodeError;

    const billingStateError = validateBillingState(cardData.billingState);
    if (billingStateError) newErrors.billingState = billingStateError;

    const billingCityError = validateBillingCity(cardData.billingCity);
    if (billingCityError) newErrors.billingCity = billingCityError;

    const billingAddressError = validateBillingAddress(cardData.billingAddress);
    if (billingAddressError) newErrors.billingAddress = billingAddressError;
  }

    // エラーがあれば状態を更新してfalseを返す
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    return true;
  };

  /**
   * カード情報お預かり登録API呼び出し
   * バックエンドで3DS認証とカード登録を自動処理
   */
  const registerCardWithBackend = async (token) => {
    const csrfToken = await getCsrfTokenFromCookie(true);
    if (!csrfToken) {
      throw new Error('CSRFトークンの取得に失敗しました');
    }

    const config = getEnvironmentConfig();
    const apiUrl = `${config.baseUrl}/api/v1/payment/card-register`;

    // 会員住所を使用する場合は会員情報から取得
    const billingData = cardData.useMemberAddress 
      ? {
          bill_address_city: memberInfo.data?.city || '',
          bill_address_line1: memberInfo.data?.address || '',
          bill_address_post_code: (memberInfo.data?.postalCode || '').replace(/-/g, ''),
          bill_address_state: getPrefectureCode(memberInfo.data?.prefecture) || '',
        }
      : {
          bill_address_city: cardData.billingCity,
          bill_address_line1: cardData.billingAddress,
          bill_address_post_code: cardData.billingZipCode.replace(/-/g, ''),
          bill_address_state: cardData.billingState,
        };

    const requestData = {
      token: token,
      cardholder_name: cardData.cardholderName,
      email_address: memberInfo.data?.email,
      ...billingData,
      bill_address_country: '392' // 日本
    };

    const response = await fetch(apiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken
      },
      body: JSON.stringify(requestData)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'カード登録に失敗しました');
    }

    return data;
  };

  /**
   * フォーム送信処理
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 初期バリデーション
    if (isSubmitting) {
      return;
    }

    if (memberInfo.loading) {
      showError('会員情報を取得中です。しばらくお待ちください。', 3000);
      return;
    }

    if (!memberInfo.memberId) {
      showError('会員情報を取得できません。ページを再読み込みしてください。', 5000);
      return;
    }
    if (!validateFormData()) {
      showError('入力内容を確認してください', 5000);
      return;
    }

    // カードブランドを判定
    const cardBrand = detectCardBrand(cardData.cardNumber);

    // 処理中画面を表示
    setProcessingState({
      isProcessing: true,
      cardBrand: cardBrand,
      message: 'カード情報を処理中...'
    });

    // ホームから来た場合はログアウトしない
    const isFromHome = location.state?.fromHome === true;
    if (!isFromHome) {
      // 登録フロー中の場合のみセッションをクリア
      await clearUserSessionForCardRegistration();
    }

    setIsSubmitting(true);

    try {
      // STEP 1: Paygentトークンを生成
      setProcessingState(prev => ({
        ...prev,
        message: 'カード情報を処理中...'
      }));

      const tokenResult = await generatePaygentToken(cardData);

      // STEP 2: バックエンドにトークンを送信
      setProcessingState(prev => ({
        ...prev,
        message: '決済処理中...'
      }));

      const result = await registerCardWithBackend(tokenResult.token);

      // STEP 3: レスポンスに応じて処理
      if (result.redirect_html || result.redirectHtml) {
        // 3DS認証が必要な場合
        setProcessingState(prev => ({
          ...prev,
          message: '本人認証画面に移動します...'
        }));

        const redirectHtml = result.redirect_html || result.redirectHtml;
        
        // 少し待ってから3DS画面を表示
        setTimeout(() => {
          display3DSScreen(redirectHtml);
        }, 1000);
        return;
      }

      // 3DS認証不要の場合（カード登録が即座に完了）
      setProcessingState(prev => ({
        ...prev,
        message: '登録完了しました'
      }));

      showSuccess('クレジットカード情報を登録しました', 5000);

      // 遷移先を分岐
      if (isFromHome) {
        // ホームから来た場合はホームに戻る
        navigate(ROUTES.HOME, {
          state: {
            message: 'クレジットカード情報を更新しました'
          }
        });
      } else {
        // 登録フロー中の場合は従来通り
        const completeData = {
          creditCard: {
            customerId: memberInfo.memberId,
            paymentAmount: storeCostInfo.cost,
            currencyCode: 'JPY',
            emailAddress: memberInfo.data?.email,
            customerCardId: result.customer_card_id || result.customerCardId,
            tradingId: result.trading_id || result.tradingId
          },
          memberId: memberInfo.memberId
        };

        if (completeStep) {
          completeStep(REGISTRATION_STEPS.CREDIT_CARD, {
            creditCardInfo: completeData.creditCard
          });
        }

        if (onCreditCardComplete) {
          onCreditCardComplete(completeData);
        } else {
          navigate(`${ROUTES.REGISTRATION_COMPLETE}?memberId=${memberInfo.memberId}`, {
            state: {
              message: 'クレジットカード登録が完了しました',
              memberData: memberInfo.data
            }
          });
        }
      }

    } catch (error) {
      console.error('[CREDIT CARD FORM] エラー発生:', error);

      // エラー時は処理中画面を非表示
      setProcessingState({
        isProcessing: false,
        cardBrand: 'UNKNOWN',
        message: ''
      });

      if (error.message.includes('トークン生成') || error.message.includes('PaygentToken')) {
        showError('カード情報の処理に失敗しました。入力内容を確認してください。', 8000);
      } else {
        const errorMessage = translateErrorMessage(error.message);
        showError(errorMessage || `予期しないエラーが発生しました: ${error.message}`, 8000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * アクセス制御: 登録フロー中またはマイページからの遷移をチェック
   */
  useEffect(() => {
    const isFromHome = location.state?.fromHome === true;
    const isInFlow = isInRegistrationFlow();

    if (!isInFlow && !isFromHome) {
      console.warn('[CREDIT CARD FORM] Unauthorized access - redirecting to home');
      showError('この画面へは正規のルートからアクセスしてください。', 5000);
      navigate('/home', { replace: true });
    }
  }, [location.state, isInRegistrationFlow, navigate, showError]);

  const { postalMessage, isSuccess, searchAddress: searchBillingAddress } = useAddressSearch(({ prefectureCode, city, address }) => {
    setCardData(prev => ({
      ...prev,
      billingState: prefectureCode,
      billingCity: city,
      billingAddress: address
    }));
  });

  /**
   * 入力フィールドのレンダリング
   */
  const renderInputField = (name, label, required = false, props = {}) => {
    const { helpText, ...inputProps } = props;
    return (
      <tr key={name} className="credit-card-form-table-row">
        <th style={registerFormStyles.th} className="credit-card-form-table-th">
          <span>{label}</span>{required && <span style={registerFormStyles.required}> *</span>}
        </th>
        <td style={registerFormStyles.td} className="credit-card-form-table-td">
          <input
            type={props.type || 'text'}
            name={name}
            value={cardData[name]}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            style={createDynamicStyles.getInputStyle(!!errors[name])}
            disabled={isSubmitting}
            {...inputProps}
          />
          {errors[name] && (
            <div style={createDynamicStyles.getMessageStyle('error')}>{errors[name]}</div>
          )}
          {props.helpText && (
            <div style={registerFormStyles.helpText}>{props.helpText}</div>
          )}
        </td>
      </tr>
    );
  };

  /**
   * ローディング表示
   */
  if (memberInfo.loading) {
    return (
      <div style={loadingStyles.container}>
        <h2>会員情報を取得中...</h2>
        <p>しばらくお待ちください</p>
      </div>
    );
  }

  /**
   * エラー表示(会員情報取得失敗時)
   */
  if (memberInfo.error) {
    return (
      <div style={errorStyles.container}>
        <h2>エラーが発生しました</h2>
        <p>{memberInfo.error}</p>
        <p>自動的にリダイレクトします...</p>
      </div>
    );
  }

  /**
   * メインフォームのレンダリング
   */
  return (
    <>
      <div style={registerFormStyles.container} className="credit-card-form-container">
        {/* 会員ID表示 */}
        {memberInfo.memberId && (
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '5px'
          }} className="member-info-display" translate="no">
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>
              会員ID
            </h3>
            <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
              {memberInfo.memberId}
            </p>
          </div>
        )}

        {/* 会員情報表示 */}
        {memberInfo.data && (
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#f0f8ff',
            borderRadius: '5px'
          }} className="member-info-display" translate="no">
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>
              登録された会員情報
            </h3>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              <strong>お名前:</strong><span>{` ${memberInfo.data.name || memberInfo.data.fullName || '未設定'}`}</span>
            </p>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              <strong>メールアドレス:</strong><span>{` ${memberInfo.data.email || '未設定'}`}</span>
            </p>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              <strong>利用店舗:</strong><span>{` ${memberInfo.data.storeName || '未設定'}`}</span>
            </p>
          </div>
        )}

        {/* メインフォーム */}
        <form onSubmit={handleSubmit} noValidate>
          <h2 style={registerFormStyles.pageTitle}>クレジットカード情報入力</h2>

          {/* 初期費用お知らせ */}
          <DynamicInitialCostNotice
            storeName={memberInfo.data?.storeName}
            costInfo={storeCostInfo}
          />

          {/* クレジットカード情報入力テーブル */}
          <table style={registerFormStyles.table} className="credit-card-form-table">
            <tbody>
              {/* カード番号入力フィールド */}
              {renderInputField('cardNumber', 'カード番号', true, {
                type: 'text',
                placeholder: '1234 5678 9012 3456',
                maxLength: '23'
              })}

              {/* カード名義入力フィールド */}
              {renderInputField('cardholderName', 'カード名義', true, {
                type: 'text',
                placeholder: 'TARO YAMADA',
                maxLength: '26',
                helpText: 'カードに記載されている名前をローマ字で入力してください'
              })}

              {/* 有効期限入力フィールド */}
              <tr className="credit-card-form-table-row">
                <th style={registerFormStyles.th} className="credit-card-form-table-th">
                  <span>有効期限</span><span style={registerFormStyles.required}> *</span>
                </th>
                <td style={registerFormStyles.td} className="credit-card-form-table-td">
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }} className="credit-card-expiry-row">
                    <select
                      name="expiryMonth"
                      value={cardData.expiryMonth}
                      onChange={handleChange}
                      style={{
                        ...registerFormStyles.select,
                        ...(errors.expiry ? createDynamicStyles.getInputStyle(true) : {})
                      }}
                      disabled={isSubmitting}
                    >
                      <option value="">月</option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                          {String(i + 1).padStart(2, '0')}
                        </option>
                      ))}
                    </select>

                    <span>/</span>

                    <select
                      name="expiryYear"
                      value={cardData.expiryYear}
                      onChange={handleChange}
                      style={{
                        ...registerFormStyles.select,
                        ...(errors.expiry ? createDynamicStyles.getInputStyle(true) : {})
                      }}
                      disabled={isSubmitting}
                    >
                      <option value="">年</option>
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = new Date().getFullYear() + i;
                        return (
                          <option key={year} value={String(year).slice(2)}>
                            {String(year).slice(2)}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {errors.expiry && (
                    <div style={createDynamicStyles.getMessageStyle('error')}>{errors.expiry}</div>
                  )}
                </td>
              </tr>

              {/* CVV入力フィールド */}
              {renderInputField('cvv', 'セキュリティコード', true, {
                type: 'text',
                placeholder: '123',
                maxLength: '4',
                helpText: 'カード裏面の3桁または4桁の番号'
              })}

              {/* 会員住所使用チェックボックス */}
              <tr className="credit-card-form-table-row">
                <th style={registerFormStyles.th} className="credit-card-form-table-th">請求先住所</th>
                <td style={registerFormStyles.td} className="credit-card-form-table-td">
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    padding: '10px 0'
                  }}>
                    <input
                      type="checkbox"
                      name="useMemberAddress"
                      checked={cardData.useMemberAddress}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span style={{ fontSize: '14px' }}>
                      会員登録情報と同じ住所を使用する
                    </span>
                  </label>
                  {memberInfo.data && cardData.useMemberAddress && (
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#666'
                    }} translate="no">
                      <div>{`〒${memberInfo.data.postalCode ?? ''}`}</div>
                      <div>{`${memberInfo.data.prefecture ?? ''} ${memberInfo.data.city ?? ''}`}</div>
                      <div>{memberInfo.data.address}</div>
                      {memberInfo.data.buildingRoom && <div>{memberInfo.data.buildingRoom}</div>}
                    </div>
                  )}
                </td>
              </tr>

              {/* 請求先住所入力フィールド（useMemberAddress=falseの時のみ表示） */}
              {!cardData.useMemberAddress && (
                <>
                  {/* 請求先郵便番号 - 住所検索機能付き */}
                  <PostalCodeInput
                    label="請求先郵便番号"
                    required
                    name="billingZipCode"
                    value={cardData.billingZipCode}
                    onChange={(e) => {
                      const formatted = formatPostalCode(e.target.value);
                      setCardData(prev => ({ ...prev, billingZipCode: formatted }));
                      if (errors.billingZipCode) {
                        setErrors(prev => ({ ...prev, billingZipCode: '' }));
                      }
                    }}
                    onSearch={searchBillingAddress}
                    error={errors.billingZipCode}
                    postalMessage={postalMessage}
                    isSuccess={isSuccess}
                    disabled={isSubmitting}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    thStyle={registerFormStyles.th}
                    tdStyle={registerFormStyles.td}
                    inputStyle={createDynamicStyles.getInputStyle(!!errors.billingZipCode)}
                    errorStyle={createDynamicStyles.getMessageStyle('error')}
                    helpText="カードの請求書が送付される住所の郵便番号を入力してください"
                    helpTextStyle={registerFormStyles.helpText}
                    requiredStyle={registerFormStyles.required}
                    rowClassName="credit-card-form-table-row"
                    thClassName="credit-card-form-table-th"
                    tdClassName="credit-card-form-table-td"
                  />

                  {/* 都道府県選択フィールド */}
                  <tr className="credit-card-form-table-row">
                    <th style={registerFormStyles.th} className="credit-card-form-table-th">
                      <span>都道府県</span><span style={registerFormStyles.required}> *</span>
                    </th>
                    <td style={registerFormStyles.td} className="credit-card-form-table-td">
                      <select
                        name="billingState"
                        value={cardData.billingState}
                        onChange={handleChange}
                        style={{
                          ...registerFormStyles.select,
                          width: '200px',
                          ...(errors.billingState ? createDynamicStyles.getInputStyle(true) : {})
                        }}
                        disabled={isSubmitting}
                      >
                        {PREFECTURES.map(pref => (
                          <option key={pref.value} value={pref.value}>
                            {pref.label}
                          </option>
                        ))}
                      </select>
                      {errors.billingState && (
                        <div style={createDynamicStyles.getMessageStyle('error')}>{errors.billingState}</div>
                      )}
                    </td>
                  </tr>

                  {/* 市区町村入力フィールド */}
                  {renderInputField('billingCity', '市区町村', true, {
                    type: 'text',
                    placeholder: '渋谷区'
                  })}

                  {/* 住所入力フィールド */}
                  {renderInputField('billingAddress', '住所', true, {
                    type: 'text',
                    placeholder: '渋谷1-2-3',
                    helpText: '番地・建物名を入力してください'
                  })}
                </>
              )}
            </tbody>
          </table>


          {/* 送信ボタン */}
          <div style={{ textAlign: 'center', marginTop: '30px' }} className="credit-card-submit-container">
            <PersonalInfoConsent />
            <button
              type="submit"
              disabled={isSubmitting || memberInfo.loading || !memberInfo.memberId}
              style={{
                ...registerFormStyles.submitButton,
                ...(isSubmitting ? registerFormStyles.submitButtonDisabled : {}),
                opacity: (isSubmitting || memberInfo.loading || !memberInfo.memberId) ? 0.6 : 1,
                cursor: (isSubmitting || memberInfo.loading || !memberInfo.memberId) ? 'not-allowed' : 'pointer'
              }}
              className="credit-card-submit-button"
            >
              {isSubmitting 
                ? BUTTON_TEXT.register.submitting 
                : BUTTON_TEXT.register.submit(storeCostInfo.cost)
              }
            </button>
          </div>

          {/* デバッグ情報表示(開発環境のみ) */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{
              marginTop: '20px',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '12px'
            }} className="debug-info">
              <strong>デバッグ情報:</strong>
              <br /><span>{`会員ID: ${memberInfo.memberId || 'なし'}`}</span>
              <br /><span>{`利用店舗: ${memberInfo.data?.storeName || 'なし'}`}</span>
              <br /><span>{`決済金額: ¥${storeCostInfo.cost.toLocaleString()}`}</span>
              <br /><span>{`PaygentToken: ${checkPaygentTokenLoaded() ? 'ロード済み' : '未ロード'}`}</span>
              <br /><span>3DS処理: バックエンド自動処理</span>
              <br /><span>{`会員住所使用: ${cardData.useMemberAddress ? 'はい' : 'いいえ'}`}</span>
            </div>
          )}
        </form>
      </div>

      {/* 離脱防止モーダル */}
      <NavigationBlockModal
        isOpen={isModalOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {/* ✅ 処理中画面 */}
      {processingState.isProcessing && (
        <PaymentProcessing
          cardBrand={processingState.cardBrand}
          message={processingState.message}
        />
      )}
    </>
  );
};

export default CreditCardForm;